import { validateLoginRequestPayload } from '@cascade/api-client/auth';
import type { TLoginCallbackRequestPayload } from '@cascade/api-client/auth';
import { getErrorReason, RequestError } from '#errors/index.ts';
import type {
  TAuthError,
  TAuthErrorReason,
  TAuthState,
  TAuthStatus,
  TAuthUser,
  TSignInCallback,
} from './auth.types.ts';

const LOGIN_EMAIL_FIELD = 'email';

const UNSUPPORTED_LOGIN_PROVIDER_REASON = 'unsupported_login_provider';

const heldSessionStatuses: TAuthStatus[] = ['authenticated', 'refreshing'];

export class AuthError extends Error {
  readonly reason: TAuthErrorReason;

  constructor(reason: TAuthErrorReason, options?: ErrorOptions) {
    super(reason, options);

    this.name = 'AuthError';
    this.reason = reason;
  }
}

const isUnsupportedLoginProvider = (error: RequestError): boolean =>
  getErrorReason(error) === UNSUPPORTED_LOGIN_PROVIDER_REASON;

export const toAuthFailure = (error: unknown): TAuthError => {
  if (error instanceof RequestError || error instanceof AuthError) {
    return error;
  }

  return new AuthError('unexpected', { cause: error });
};

export const toProviderFailure = (error: unknown): TAuthError => {
  if (error instanceof RequestError && isUnsupportedLoginProvider(error)) {
    return new AuthError('unsupported_provider', { cause: error });
  }

  return toAuthFailure(error);
};

export const hasSession = (state: TAuthState): boolean =>
  heldSessionStatuses.includes(state.status);

export const isBusy = (state: TAuthState): boolean => {
  if (state.status === 'signing-in') {
    return !state.magicAuth;
  }

  return (
    state.status === 'restoring' ||
    state.status === 'refreshing' ||
    state.status === 'signing-out'
  );
};

export const getCallbackCode = (
  callback?: TSignInCallback,
): string | undefined => {
  if (!callback) {
    return undefined;
  }

  return 'code' in callback ? callback.code : callback.magicAuthCode;
};

export const toCallbackPayload = (
  callback: TSignInCallback,
  email?: string,
): TLoginCallbackRequestPayload =>
  'code' in callback
    ? { code: callback.code }
    : { magic_auth_code: callback.magicAuthCode, email };

export const getDisplayName = (user?: TAuthUser): string => {
  if (!user) {
    return '';
  }

  if (user.name) {
    return user.name;
  }

  const [emailHandle] = user.email.split('@');

  return emailHandle;
};

export const isValidLoginEmail = (email: string): boolean => {
  const details = validateLoginRequestPayload({ email: email.trim() });

  return !details.some(({ field }) => field === LOGIN_EMAIL_FIELD);
};

export const canStartSignIn = (state: TAuthState): boolean =>
  state.status === 'anonymous' && state.reason !== 'failed';
